import { useState } from 'react';
import type { GrantRole, PublicMode, SharingResponse } from './api/types';
import { useMessages } from './i18n/context';

const MODES: readonly PublicMode[] = ['inherit', 'private', 'public'];
const ROLES: readonly GrantRole[] = ['reader', 'writer', 'none'];

/**
 * The owner's sharing panel (ADR-027 §3/§4/§6): the public mode with its share link (and the
 * rotation that retires it), the people with a grant and their roles, and the add-grant form.
 * Purely presentational over the latest server view — every change round-trips through the
 * API upstairs and the panel re-renders from the answer.
 */
export function ShareDialog({
  sharing,
  busy,
  origin,
  onSetPublic,
  onRotate,
  onSetGrant,
  onRemoveGrant,
  onClose,
}: {
  /** The owner's view, or null while it loads. */
  sharing: SharingResponse | null;
  busy: boolean;
  /** The SPA's origin — share links are `{origin}/p/{shareId}`. */
  origin: string;
  onSetPublic: (mode: PublicMode) => void;
  onRotate: () => void;
  onSetGrant: (email: string, role: GrantRole) => void;
  onRemoveGrant: (email: string) => void;
  onClose: () => void;
}) {
  const t = useMessages();
  const g = t.grove;
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<GrantRole>('reader');
  const trimmed = email.trim();

  return (
    <section className="dialog-panel share-dialog" role="dialog" aria-labelledby="share-title">
      <div className="dialog-heading">
        <div>
          <p className="eyebrow">{g.collaborate}</p>
          <h3 id="share-title">{sharing !== null ? t.share.headingFor(sharing.name) : t.share.heading}</h3>
        </div>
        <button className="button-primary" disabled={busy} onClick={onClose}>
          {t.share.done}
        </button>
      </div>
      {sharing === null ? (
        <p className="empty-state">{t.share.loading}</p>
      ) : (
        <>
          <p className="muted">{g.sharingDescription}</p>
          <div className="sharing-visibility">
            <label className="field">
              {t.share.visibility}{' '}
              <select
                aria-label={t.share.publicMode}
                value={sharing.public}
                disabled={busy}
                onChange={(e) => onSetPublic(e.target.value as PublicMode)}
              >
                {MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t.share.mode[mode]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {sharing.shareId !== null && (
            <div className="inline-form share-link-field">
              <label className="field">
                {t.share.linkLabel}{' '}
                <input aria-label={t.share.shareLink} readOnly value={`${origin}/p/${sharing.shareId}`} />
              </label>
              <button className="button-secondary" disabled={busy} onClick={onRotate}>
                {t.share.rotateLink}
              </button>
            </div>
          )}
          <h4>{t.share.people}</h4>
          {sharing.grants.length === 0 ? (
            <p className="empty-state">{t.share.nobodyElse}</p>
          ) : (
            <ul className="grant-list">
              {sharing.grants.map((grant) => (
                <li key={grant.email}>
                  <span className="grant-email">{grant.email}</span>
                  <select
                    aria-label={t.share.roleFor(grant.email)}
                    value={grant.role}
                    disabled={busy}
                    onChange={(e) => onSetGrant(grant.email, e.target.value as GrantRole)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t.share.grantRoles[r]}
                      </option>
                    ))}
                  </select>{' '}
                  <button
                    className="button-danger button-quiet"
                    aria-label={t.share.remove(grant.email)}
                    disabled={busy}
                    onClick={() => onRemoveGrant(grant.email)}
                  >
                    {t.share.removeButton}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form
            className="inline-form grant-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (trimmed !== '') {
                onSetGrant(trimmed, role);
                setEmail('');
              }
            }}
          >
            <label className="field">
              {g.emailAddress}
              <input
                aria-label={t.share.grantEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.share.emailPlaceholder}
                disabled={busy}
              />
            </label>
            <label className="field">
              {g.permission}
              <select
                aria-label={t.share.grantRole}
                value={role}
                disabled={busy}
                onChange={(e) => setRole(e.target.value as GrantRole)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t.share.grantRoles[r]}
                  </option>
                ))}
              </select>
            </label>
            <button className="button-primary" type="submit" disabled={busy || trimmed === ''}>
              {t.share.add}
            </button>
          </form>
        </>
      )}
    </section>
  );
}
